import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  EventEmitter,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { Subject, of } from 'rxjs';
import { switchMap, takeUntil, map, catchError } from 'rxjs/operators';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';

import { IdentityReportsService } from '@app/core/services/identity-reports.service';
import { Client } from '@app/core/types/client';
import { IdentityDocument } from '@app/core/types/client-document';
import { BrandCodes } from '@app/core/types/brand';

@Component({
  selector: 'itfg-identity-document-viewer',
  templateUrl: './identity-document-viewer.component.html',
  styleUrl: './identity-document-viewer.component.scss',
  standalone: false,
})
export class IdentityDocumentViewerComponent implements OnInit, OnChanges, OnDestroy {

  // ── Inputs ─────────────────────────────────────────────────────────

  @Input() client!: Client;
  @Input() label = '';
  @Input() documentType!: string;
  @Input() selectedDocId: number | null = null;

  // ── Outputs ────────────────────────────────────────────────────────

  @Output() documentSelected = new EventEmitter<IdentityDocument | null>();

  // ── Template ref ───────────────────────────────────────────────────

  @ViewChild('viewerModalTpl') private viewerModalTpl!: TemplateRef<unknown>;

  // ── Thumbnail preview — locked to the verification doc ─────────────
  // Never changes when the admin browses inside the modal.

  previewUrl: string | null = null;
  previewLoading = false;
  previewError = false;

  // ── Modal image — changes freely as the admin browses ───────────────

  modalUrl: string | null = null;
  prevModalUrl: string | null = null;
  modalLoading = false;
  modalError = false;

  // ── Pinned image state ─────────────────────────────────────────────

  pinnedDoc: IdentityDocument | null = null;
  pinnedImageUrl: string | null = null;
  loadingPinned = false;
  errorPinned = false;

  // ── List / selection state ─────────────────────────────────────────

  docList: IdentityDocument[] = [];
  loadingList = false;
  currentDoc: IdentityDocument | null = null;

  private readonly _blobCache = new Map<number, Blob>();
  private readonly _docLoad$ = new Subject<IdentityDocument | null>();
  private readonly _unsubscribe = new Subject<void>();
  private _dialogRef: MatDialogRef<unknown> | null = null;

  constructor(
    private readonly service: IdentityReportsService,
    private readonly dialog: MatDialog,
  ) { }

  // ── Lifecycle ──────────────────────────────────────────────────────

  ngOnInit(): void {
    // Drives only the modal selected panel
    this._docLoad$
      .pipe(
        takeUntil(this._unsubscribe),
        switchMap(doc => {
          this.modalError = false;

          if (!doc) {
            this._resetModal();
            return of<Blob | null>(null);
          }

          if (doc.id === this.currentDoc?.id && this.modalUrl) {
            return of<Blob | null>(null);
          }

          this.prevModalUrl = this.modalUrl;
          this._revokeModalUrl();
          this.currentDoc = doc;

          const cached = this._blobCache.get(doc.id);
          if (cached) {
            this.prevModalUrl = null;
            this.modalUrl = URL.createObjectURL(cached);
            return of<Blob | null>(null);
          }

          this.modalLoading = true;
          this.modalUrl = null;

          return this.service
            .renderIdentityDocumentContent(this.client.civilId, doc.id)
            .pipe(
              map((raw: Blob) => {
                const blob = new Blob([raw as any]);
                this._blobCache.set(doc.id, blob);
                return blob;
              }),
              catchError(err => {
                console.error('[IdentityDocumentViewer]', err);
                this.modalError = true;
                this.prevModalUrl = null;
                return of<Blob | null>(null);
              }),
            );
        }),
      )
      .subscribe(blob => {
        if (blob) {
          this.modalUrl = URL.createObjectURL(blob);
          this.prevModalUrl = null;
        }
        this.modalLoading = false;
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['client'] && this.client?.civilId) {
      this._loadList();
    } else if (changes['selectedDocId']) {
      const doc = this.docList.find(d => d.id === this.selectedDocId) ?? null;
      if (!doc) {
        this._loadList()
      } else {
        this._syncSelectedDoc();
      }
    }
  }

  ngOnDestroy(): void {
    this._dialogRef?.close();
    this._revokeModalUrl();
    this._revokePreviewUrl();
    this._revokePinnedImage();
    this._unsubscribe.next();
    this._unsubscribe.complete();
  }

  // ── Public actions ─────────────────────────────────────────────────

  openModal(): void {
    this._dialogRef = this.dialog.open(this.viewerModalTpl, {
      autoFocus: false,
      panelClass: 'idv-dialog-panel',
    });
  }

  selectDoc(doc: IdentityDocument): void {
    this._docLoad$.next(doc);
    this.documentSelected.emit(doc);
  }

  pinDoc(doc: IdentityDocument, event: Event): void {
    event.stopPropagation();

    if (this.pinnedDoc?.id === doc.id) {
      this._revokePinnedImage();
      this.pinnedDoc = null;
      return;
    }

    this._revokePinnedImage();
    this.pinnedDoc = doc;
    this.errorPinned = false;

    const cached = this._blobCache.get(doc.id);
    if (cached) {
      this.pinnedImageUrl = URL.createObjectURL(cached);
      return;
    }

    this.loadingPinned = true;

    this.service
      .renderIdentityDocumentContent(this.client.civilId, doc.id)
      .pipe(takeUntil(this._unsubscribe))
      .subscribe({
        next: (raw: Blob) => {
          const blob = new Blob([raw as any]);
          this._blobCache.set(doc.id, blob);
          if (this.pinnedDoc?.id === doc.id) {
            this.pinnedImageUrl = URL.createObjectURL(blob);
          }
          this.loadingPinned = false;
        },
        error: err => {
          console.error('[IdentityDocumentViewer] pin load error', err);
          if (this.pinnedDoc?.id === doc.id) {
            this.errorPinned = true;
          }
          this.loadingPinned = false;
        },
      });
  }

  // ── Private ────────────────────────────────────────────────────────

  private _loadList(): void {
    this.loadingList = true;

    this.service
      .getIdentityDocumentList$(this.client.civilId, this.client.brand.code as BrandCodes)
      .pipe(takeUntil(this._unsubscribe))
      .subscribe({
        next: (docs: IdentityDocument[]) => {
          this.docList = docs;
          this._syncSelectedDoc();
        },
        error: err => console.error(err),
        complete: () => { this.loadingList = false; },
      });
  }

  private _syncSelectedDoc(): void {
    if (this.selectedDocId) {
      const doc = this.docList.find(d => d.id === this.selectedDocId) ?? null;
      if (doc) {
        this._loadPreview(doc);  // thumbnail — set once, never touched by browsing
        this.selectDoc(doc);     // pre-select the same doc in the modal
      } else {
        this._resetAll();
        this.documentSelected.emit(null);
      }
    } else {
      this._resetAll();
      this.documentSelected.emit(null);
    }
  }

  /** Loads the thumbnail image. Uses the blob cache so no duplicate request. */
  private _loadPreview(doc: IdentityDocument): void {
    this.previewError = false;

    const cached = this._blobCache.get(doc.id);
    if (cached) {
      this._revokePreviewUrl();
      this.previewUrl = URL.createObjectURL(cached);
      return;
    }

    this.previewLoading = true;

    this.service
      .renderIdentityDocumentContent(this.client.civilId, doc.id)
      .pipe(takeUntil(this._unsubscribe))
      .subscribe({
        next: (raw: Blob) => {
          const blob = new Blob([raw as any]);
          this._blobCache.set(doc.id, blob);
          this._revokePreviewUrl();
          this.previewUrl = URL.createObjectURL(blob);
          this.previewLoading = false;
        },
        error: () => {
          this.previewError = true;
          this.previewLoading = false;
        },
      });
  }

  private _resetAll(): void {
    this._resetModal();
    this._revokePreviewUrl();
    this.previewLoading = false;
    this.previewError = false;
  }

  private _resetModal(): void {
    this._revokeModalUrl();
    this.prevModalUrl = null;
    this.currentDoc = null;
    this.modalLoading = false;
  }

  private _revokeModalUrl(): void {
    if (this.modalUrl) {
      URL.revokeObjectURL(this.modalUrl);
      this.modalUrl = null;
    }
  }

  private _revokePreviewUrl(): void {
    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl);
      this.previewUrl = null;
    }
  }

  private _revokePinnedImage(): void {
    if (this.pinnedImageUrl) {
      URL.revokeObjectURL(this.pinnedImageUrl);
      this.pinnedImageUrl = null;
    }
  }
}